import { Fragment } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import { CheckIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import EnsureDataPrivacyImage from './EnsureDataPrivacyImage'

const copy = {
  title: `Ensure data privacy, security, and compliance`,
  subtitle: `We’ve built all the tools you need to manage and secure your customer data—easily and at scale.`,
  elements: [
    {
      title: `A proactive approach to data privacy`,
      description: `Get real-time visibility into what personal information you’re collecting, where you’re collecting it from, and where you’re sending it. Then, set rules to automatically protect it.`,
    },
    {
      title: `Streamline data compliance`,
      description: `Accelerate compliance with regulations like the GDPR and the CCPA with automated data subject rights management and our open source consent manager.`,
    },
    {
      title: `Activate customer data with Segment`,
      description: `Over 25,000 companies trust Segment to connect, unify, and activate their customer data to drive business results:`,
      subList: [
        <Fragment key={1}>
          <b>Real-time:</b> 12,696 Unique events tracked
        </Fragment>,
        <Fragment key={2}>
          <b>Scale:</b> 12.1 Trillion API calls processed in 2023
        </Fragment>,
        <Fragment key={3}>
          <b>Reliability:</b> 99.9999% uptime
        </Fragment>,
      ],
    },
  ],
  ctaUrl: `/product/privacy/`,
  ctaText: `Explore Privacy Portal`,
}

const EnsureDataPrivacy = () => {
  const pageConfig = usePageConfig()
  const theme = useTheme()
  return (
    <Wrapper>
      <StyledSectionBaseTitle variant="noCopy">
        <Section.Contents>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.title}</CopyText.Heading>
            <CopyText.Description>{copy.subtitle}</CopyText.Description>
          </CopyTextBase>
        </Section.Contents>
      </StyledSectionBaseTitle>
      <StyledSectionBaseContent reverse={true} variant="2Col">
        <Section.Contents>
          <EnsureDataPrivacyImage />
        </Section.Contents>
        <Section.Copy alignSelf="center">
          <ColumnTrack>
            {copy.elements.map(({ title, description, subList }, key) => (
              <StyledFeatureBoxBase key={key} alignment="left" appearance="twilioLight" headerPosition="left">
                <FeatureBox.Header>
                  <StyledCheckIcon size="small" />
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <FeatureBox.Title>{title}</FeatureBox.Title>
                  <FeatureBox.Text
                    tokens={{
                      color: theme.palette.twilio.gray[80],
                    }}
                    variant="pMedium"
                  >
                    {description}
                  </FeatureBox.Text>
                  {subList && (
                    <SubListWrapper>
                      {subList.map((item, key) => (
                        <StyledList key={key}>
                          <StyledListItem>{item}</StyledListItem>
                        </StyledList>
                      ))}
                    </SubListWrapper>
                  )}
                </FeatureBox.Body>
              </StyledFeatureBoxBase>
            ))}
            <CopyTextBase>
              <CopyText.Link href={getAbsoluteUrl(copy.ctaUrl, pageConfig)}>{copy.ctaText}</CopyText.Link>
            </CopyTextBase>
          </ColumnTrack>
        </Section.Copy>
      </StyledSectionBaseContent>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const SubListWrapper = styled.div``

const StyledList = styled.ul`
  ${({ theme }) => theme.typography.pMedium};
  padding-left: 9px;
  margin: 8px 0;
  list-style-position: inside;
  list-style-type: square;
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
`

export const StyledListItem = styled.li`
  white-space: nowrap;
  max-width: 100%;
  margin: 8px 0;

  &,
  &::marker {
    ${({ theme }) => theme.typography.pMedium.css};
  }
`

const StyledCheckIcon = styled(CheckIcon)`
  margin-top: 5px;
`

const StyledFeatureBoxBase = styled(FeatureBoxBase)`
  gap: 0;
  grid-template-columns: 22px auto;
`

const StyledSectionBaseTitle = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 50px;
    padding-bottom: 0;
  }
`

const StyledSectionBaseContent = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 60px;
    padding-bottom: 50px;
  }
`

export default EnsureDataPrivacy
